import React from 'react';
import { Claim } from 'src/types/verification';
import { useClaimHighlight } from 'src/hooks/useUIConfig';

interface ClaimHighlighterProps {
  text: string;
  claims: Claim[];
}

export const ClaimHighlighter: React.FC<ClaimHighlighterProps> = ({ text, claims }) => {
  const { shouldHighlight, getHighlightColor } = useClaimHighlight();
  
  if (!claims || claims.length === 0) return <>{text}</>;
  
  // Create an array of claims with text_span and their positions in the text
  const claimPositions: Array<{ claim: Claim; start: number; end: number }> = [];
  
  claims.forEach(claim => {
    if (claim.text_span && shouldHighlight(claim.assessment)) {
      let searchIndex = 0;
      // Find all occurrences of the text_span in the text
      while (searchIndex < text.length) {
        const index = text.indexOf(claim.text_span, searchIndex);
        if (index === -1) break;
        
        claimPositions.push({
          claim,
          start: index,
          end: index + claim.text_span.length
        });
        
        searchIndex = index + 1;
      }
    }
  });
  
  // If no claims should be highlighted, return the original text
  if (claimPositions.length === 0) return <>{text}</>;
  
  // Sort by start position to process in order
  claimPositions.sort((a, b) => a.start - b.start);
  
  // Remove overlapping positions (keep the first one)
  const nonOverlappingPositions = [];
  let lastEnd = -1;
  
  for (const pos of claimPositions) {
    if (pos.start >= lastEnd) {
      nonOverlappingPositions.push(pos);
      lastEnd = pos.end;
    }
  }
  
  // Build the result with highlighted sections
  const result = [];
  let lastIndex = 0;
  
  for (const { claim, start, end } of nonOverlappingPositions) {
    // Add text before the current claim
    if (start > lastIndex) {
      result.push(text.substring(lastIndex, start));
    }
    
    // Get highlight color based on assessment
    const highlightColor = getHighlightColor(claim.assessment);
    
    // Add the highlighted claim text
    result.push(
      <span 
        key={`claim-${start}-${end}`}
        className={`${
          highlightColor === 'green'
            ? 'bg-green-100 border-b-2 border-green-500' 
            : highlightColor === 'red'
              ? 'bg-red-100 border-b-2 border-red-500'
              : 'bg-gray-100 border-b-2 border-gray-500'
        }`}
        title={`${
          claim.assessment === 'TRUE' 
            ? 'Verified' 
            : claim.assessment === 'FALSE'
              ? 'Disputed'
              : 'No Consensus'
        }: ${claim.claim}`}
      >
        {text.substring(start, end)}
      </span>
    );
    
    lastIndex = end;
  }
  
  // Add any remaining text
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  
  return <>{result}</>;
}; 